import {Component, OnInit, OnDestroy, ViewChild, ElementRef}    from '@angular/core';
import {FormBuilder, FormGroup, Validators}                     from "@angular/forms";
import {NgbActiveModal}                                         from "@ng-bootstrap/ng-bootstrap";
import {HttpErrorResponse}                                      from "@angular/common/http";

import {AngularQuery}                                           from "../../services/query/angularquery";
import {Message}                                                from "../../services/message/message";
import {MessageEntry}                                           from "../../services/message/message-entry";
import {Permissions}                                            from "../../services/permissions/permissions";
import {Phrase}                                                 from "../../services/tag/phrase";
import {TagService}                                             from "../../services/tag/tag.service";

import {Observable}                                             from "rxjs";
import {Subscription}                                           from "rxjs/Subscription";
import {isNullOrUndefined}                                      from "util";



@Component({
	selector: 'app-phrases-admin',
	templateUrl: './phrases-admin.component.html',
	styleUrls: ['./phrases-admin.component.css']
})



/**
 * Allow the editing, creation and deletion of search phrases that are
 * used when scanning article text for Tag words.
 */
export class PhrasesAdminComponent implements OnInit, OnDestroy {



	addedPhrases:Phrase[] = [];
	addedTags:string[] = [];
	activePhraseList:Phrase[] = [];
	deletedPhrases:Phrase[] = [];
	message: Message;
	search: Phrase[] = [];
	searchFound: Subscription;
	tForm: FormGroup;
	tagList: string[] = [];



	/**
	 * Initialize the component.
	 *
	 * @param {NgbActiveModal} activeModal       Used to close the page.
	 * @param {FormBuilder} fb                   Used to create the page objects.
	 * @param {TagService} tagService            Used when retrieving the search phrases.
	 */
	constructor(
		public activeModal: NgbActiveModal,
		private fb: FormBuilder,
		private tagService: TagService,
	) {}



	/**
	 * Unsubscribe from any active Subscriptions to avoid memory leaks.
	 */
	ngOnDestroy() {

		if (this.searchFound) {

			this.searchFound.unsubscribe();

		}

	}


	/**
	 * Load the phrases and initialize everything for display.
	 */
	ngOnInit() {

		// Initialize the data structure for the page.
		this.tForm = this.fb.group({
			id: [-1, [Validators.required, Validators.min(-1)]],
			search: ['', [Validators.required,Validators.minLength(1), Validators.maxLength(32),]],
			tag: ['', [Validators.required,Validators.minLength(1), Validators.maxLength(6),]],
		});

		// Set up the message window with an initial message.
		let me:MessageEntry = new MessageEntry();
		me.message = 'Loading...';
		me.level = 1;
		this.message = new Message(me);
		this.message.show();

		// Retrieve 600 or fewer of the search Phrase objects.  concatMap() performs each query in sequence.
		this.searchFound = Observable
			.range(0,5)  // Retrieves 600 or fewer by outputting a sequence of ascending values.
			.concatMap((val) => {
				// Those values are Page arguments for the query.
				return this.tagService.getSearchPhrases(val);
			})
			.subscribe(

				(response) => {

					this.search = this.search.concat(response);
					me.message = 'Retrieved ' + this.search.length + ' existing searches';
					me.level = 2;
					this.message.add(me);
					this.message.show();
					this.onPhraseLoadComplete();

				},

				(err) => {

					if (! (err instanceof HttpErrorResponse) || 404 !== err.status) {

						me.message = 'Unable to retrieve existing searches';
						me.level = 4;
						this.message.add(me);
						this.message.show();

					}

				},

				() => {} // This will NEVER get called because it always stops after the first 404.
				         // Put a catch on the call to the TagService if you need an onComplete.

		);

	}

	/**
	 * Lets me talk to the Add Phrase INPUT control.
	 */
	@ViewChild('addPhraseInput')
	addPhraseInput:any;


	/**
	 * Lets me talk to the Create Tag INPUT control.
	 */
	@ViewChild('createTagInput')
	createTagInput:any;


	/**
	 * Lets me talk to the Tag SELECT control.
	 */
	@ViewChild('tagSelect')
	tagSelect: ElementRef;


	/**
	 * Add a search phrase to the list of search phrases
	 * for the current tag.
	 *
	 * @param {string} search_string  The search phrase to add.
	 */
	addPhrase(search_string:string) {

		if (isNullOrUndefined(search_string) || 0 === search_string.trim().length) {

			return;

		}

		let phrase:Phrase = new Phrase();
		phrase['search'] = search_string;
		phrase['id'] = -1;
		phrase['tag'] = this.tagSelect.nativeElement.selectedOptions[0].value;
		this.search.push(phrase);
		this.addedPhrases.push(phrase);
		this.limitPhraseList(this.tagSelect.nativeElement.selectedOptions[0].value);
		this.addPhraseInput.nativeElement.value = '';
		this.tForm.markAsDirty();

	}


	/**
	 * Add a tag to the list of tags and choose it
	 * in the SELECT control.
	 *
	 * @param {string} tag  The tag to add.
	 */
	addTag(tag:string) {

		if (isNullOrUndefined(tag) || 0 === tag.trim().length) {

			return;

		}

		this.addedTags.push(tag);
		this.tagList.push(tag);
		this.createTagInput.nativeElement.value = '';
		this.limitPhraseList(tag);
		this.tForm.markAsDirty();

		// HACK -- this lets the SELECT update in a different update cycle from the changes made above.
		setTimeout(()=>{
			this.tagSelect.nativeElement.selectedIndex = this.tagList.indexOf(tag);
		}, 0);

	}


	/**
	 * Don't allow the user to close the window if they have unsaved changes.
	 */
	close():void {

		if (! this.tForm.pristine) {

			if (false === confirm("There are unsaved changes. Click 'Cancel' and save your work or 'OK' to abandon the changes")) {

				return;

			}

		}

		this.activeModal.close('Close method');

	}


	/**
	 * Return true if the current user is an administrator.
	 * @return {boolean} True if administrator, false otherwise.
	 */
	isAdministrator():boolean {

		return Permissions.checkPermission(Permissions.SUPER_USER);

	}


	/**
	 * Filter the list of Phrase objects to only those
	 * matching the tag provided.
	 *
	 * @param {string} tag  The choice to match in the list.
	 */
	limitPhraseList(tag:string):void {

		this.activePhraseList = this.search.filter(

			function(element, index, array) {

				return element.tag === tag;

			}

		);

	}


	/**
	 * Perform any setup necessary after the Phrases are loaded
	 * from the ReST API.
	 */
	onPhraseLoadComplete() {

		if (0 !== this.search.length) {

			for (let phrase of this.search) {

				if (-1 === this.tagList.indexOf(phrase.tag)) {

					this.tagList.push(phrase.tag);

				}

			}

			this.tagList.sort();
			this.onTagSelectChange(this.tagList[0]);

		}

	}


	/**
	 * This is triggered when the user makes a new choice
	 * in the Tag SELECT control.
	 *
	 * @param {string} tag  The new choice in the Tag SELECT.
	 */
	onTagSelectChange(tag:string) {

		this.limitPhraseList(tag);

	}


	/**
	 * Remove the selected phrase from the list of active
	 * phrases.
	 *
	 * @param {Phrase} phrase  The phrase to remove.
	 */
	removePhrase(phrase:Phrase) {

		// If a phrase was added and then removed before saving, take it out of the added list.
		if (-1 !== this.addedPhrases.indexOf(phrase)) {

			this.addedPhrases.splice(this.addedPhrases.indexOf(phrase), 1);

		} else {

			this.deletedPhrases.push(phrase);

		}

		this.search.splice(this.search.indexOf(phrase), 1);
		this.limitPhraseList(this.tagSelect.nativeElement.selectedOptions[0].value);
		this.tForm.markAsDirty();

	}


	/**
	 * Save any changes made since the last time I saved.
	 */
	saveChanges():void {

		// Create the post query.
		let query:AngularQuery = new AngularQuery();
		query.setEndpoint(AngularQuery.ENDPOINT_SEARCH_PHRASES);
		query.useParams(false);
		query.useCacheBuster(false);

		// Create the post object.
		let post:Object = {};
		post['added_tags'] = this.addedTags;
		post['added_phrases'] = this.addedPhrases;
		post['deleted_phrases'] = this.deletedPhrases;

		// Update the user.
		let me:MessageEntry = new MessageEntry();
		me.message = 'Saving ...';
		me.level = 1;
		this.message = new Message(me);
		this.message.show();

		// Make it happen.
		this.searchFound = this.tagService.post(query.getUrl(), JSON.stringify(post)).subscribe(

			() => {

				this.addedPhrases.length = 0;
				this.addedTags.length = 0;
				this.deletedPhrases.length = 0;
				this.tForm.markAsPristine();

				me.message = 'Saved successfully';
				me.level = 2;
				this.message = new Message(me);
				this.message.show();

			},

			() => {

				me.message = 'Unable to save changes';
				me.level = 4;
				this.message = new Message(me);
				this.message.show();

			},

		);

	}

}



// WEBPACK FOOTER //
// ./src/app/admin/phrases/phrases-admin.component.ts